/**
 * Which transcription backend a desktop upload path should actually use.
 *
 * The v2 flag is a request, not an answer: v2 needs somewhere to POST to, and
 * the EKS base URL is absent when the build has no environment variable for it
 * or the region has no EKS stack. Claiming v2 without one is not "try v2 and
 * fall back" — Rust refuses the pair rather than split one session's chunks
 * across two services, and on the background paths that refusal is durable: it
 * lands on the queued session as a sync failure the clinician sees, on a
 * session that would have uploaded fine over v1.
 *
 * This is one function because it was three copies of two lines, and the copy
 * that was missing is what broke background sync.
 *
 * The URL is resolved in here rather than passed in, so no call site can reach
 * for the wrong resolver: it must be {@link getTranscriptionBaseUrl}, the EKS
 * internal `/v1` path that Android's `TRANSCRIPTION_V2_BASE` uses for this
 * migration, and never `getTranscriptionBaseUrlV2()`, whose `/v2` path is an
 * unrelated feature used by web live recording. Callers log their own warning
 * off `didFallBackToV1`, because the message names the surface and the surfaces
 * do not share a logger shape.
 */
import { getTranscriptionBaseUrl } from '@/lib/SessionRecordingProvider/t11nClient';

/**
 * Datadog facet for a v2-to-v1 fallback, shared by all three adopting surfaces.
 *
 * Declared once for the same reason the rule is: three copies of the condition
 * is how the missing one went unnoticed, and three differently-worded log lines
 * is the same problem one layer out. Without a stable key, "how many desktop
 * clients fell back this week, and on which surface?" takes three substring
 * queries and cannot be facetted. Pair it with a `surface` field; the per-surface
 * wording stays, because only the call site can name the surface in prose.
 */
export const TRANSCRIPTION_V2_FALLBACK_TAG = 'transcription_v2_fallback';

export interface EffectiveTranscriptionService {
  /**
   * The value to send to Rust and to branch on. True only when v2 was asked
   * for AND a base URL resolved.
   */
  effectiveUseV2: boolean;
  /**
   * The resolved EKS base URL, or `null` when none is configured. An empty
   * string is normalised to `null` — Rust treats the two the same, and an empty
   * string would otherwise build a relative `/{session}/transcribe-chunk` URL.
   */
  transcriptionBaseUrl: string | null;
  /**
   * v2 was requested and is not happening. Worth a log line at the call site:
   * it means a cohort that should be on v2 is silently on v1, which is
   * invisible from the outside.
   */
  didFallBackToV1: boolean;
}

export function resolveEffectiveTranscriptionService(
  useTranscriptionServiceV2: boolean
): EffectiveTranscriptionService {
  const transcriptionBaseUrl = getTranscriptionBaseUrl() || null;
  const effectiveUseV2 = useTranscriptionServiceV2 && transcriptionBaseUrl !== null;

  return {
    effectiveUseV2,
    transcriptionBaseUrl,
    didFallBackToV1: useTranscriptionServiceV2 && !effectiveUseV2,
  };
}
